import { useContext } from "react";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Loading } from "@analyticsMD/quil";
import MalnutritionAPIContext from "../../contexts/MalnutritionAPIContext";
import EmbededAppContainer from "../embedded-container";
import ErrorPage from "../error-page";
import MalnutritionSections from "./malnutrition-sections";
import theme from "../../theme";
import { Sizes } from "../../types/loadingSizes";
import { SolutionClasses } from "../../types/SolutionClasses";
import PlaceholderSection from "../sections/placeholder-section";

import type { FC, ReactNode } from "react";

interface MalnutritionCardContentProps {
  onClose?: () => void;
  onExpand?: () => void;
  closeButtonCmpt?: ReactNode;
}

const MalnutritionCardContent: FC<MalnutritionCardContentProps> = ({
  onClose,
  onExpand,
  closeButtonCmpt,
}) => {
  const { riskAssessmentQuery, weightsQuery, configurationQuery } = useContext(
    MalnutritionAPIContext
  );

  // The configuration picks the identifier the header shows, so the card waits
  // for it, as QCard does.
  const queries = [riskAssessmentQuery, weightsQuery, configurationQuery];
  // Disabled queries (no emr params, or the configuration in mock mode) sit in
  // `idle`, never success/error — treat them as settled so the empty state
  // renders instead of a perpetual spinner.
  const settled = queries.every((q) => q?.isSuccess || q?.isError || q?.isIdle);
  const anyErrors = queries.some((q) => q?.isError);

  // Both endpoints come back null when there's nothing for the patient, and
  // the two are independent — so treat each as optional rather than assuming
  // a successful query has data.
  const assessment = riskAssessmentQuery?.data ?? null;
  const weights = weightsQuery?.data ?? null;
  const hasContent = assessment !== null || weights !== null;

  // Only a summary without CGCA means the patient doesn't qualify. With no
  // summary (a patient PE doesn't know) the empty state below applies, and a
  // failed request shows the error page.
  const isIneligible =
    assessment !== null &&
    !assessment.patient.eligible_classes.includes(
      SolutionClasses.CARE_GAPS_AUTOMATION
    );

  if (settled && !riskAssessmentQuery?.isError && isIneligible) {
    const placeholderText = `This patient does not qualify for the Malnutrition QCard. 
                             For patients that do (Inpatient or Observation patients 18+), 
                             this panel will display relevant nutrition information.`;
    return (
      <PlaceholderSection
        placeholderText={placeholderText}
        closeButtonCmpt={closeButtonCmpt}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <EmbededAppContainer>
        <Box sx={{ minWidth: "20rem" }}>
          {!settled && !anyErrors && (
            <Box sx={{ marginTop: "25%" }} data-testid="malnutrition-loading">
              <Loading message="Loading..." logoSize={Sizes.Medium} />
            </Box>
          )}
          {anyErrors && (
            <ErrorPage
              error={
                new Error(
                  queries
                    .filter((q) => q?.isError)
                    .map((q) => String(q?.error))
                    .join(", ")
                )
              }
              closeButtonCmpt={closeButtonCmpt}
            />
          )}
          {settled && !anyErrors && !hasContent && (
            <Box
              data-testid="malnutrition-empty"
              sx={{
                padding: "2rem 1.5rem",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              No malnutrition data for this patient.
            </Box>
          )}
          {settled && !anyErrors && hasContent && (
            <MalnutritionSections
              assessment={assessment}
              weights={weights}
              onClose={onClose}
              onExpand={onExpand}
              closeButtonCmpt={closeButtonCmpt}
            />
          )}
        </Box>
      </EmbededAppContainer>
    </ThemeProvider>
  );
};

export default MalnutritionCardContent;
